/**
 * useGedcomImportPlan Hook
 *
 * Manages import plan generation from parsed GEDCOM data.
 * Transforms validated preview data into normalized import-ready structures.
 *
 * Part of Phase 4 - Slice 6: Data Transformation
 */

import { useState } from 'react';
import type { ParseResult } from './useGedcomParse';
import type { GedcomImportPlan } from '../types/GedcomImportPlan';
import { buildImportPlan } from '../utils/buildImportPlan';

/**
 * Hook state
 */
export type ImportPlanState = 'idle' | 'building' | 'ready' | 'error';

/**
 * Hook interface
 */
interface UseGedcomImportPlanReturn {
  /** Current state */
  state: ImportPlanState;

  /** Import plan (null until built) */
  plan: GedcomImportPlan | null;

  /** Build plan from parse result */
  buildPlan: (parseResult: ParseResult) => void;

  /** Reset hook state */
  reset: () => void;

  /** Error message if build fails */
  error: string | null;
}

/**
 * Custom hook for building import plan from parsed GEDCOM data
 */
export const useGedcomImportPlan = (): UseGedcomImportPlanReturn => {
  const [state, setState] = useState<ImportPlanState>('idle');
  const [plan, setPlan] = useState<GedcomImportPlan | null>(null);
  const [error, setError] = useState<string | null>(null);

  /**
   * Build import plan from parse result
   */
  const buildPlan = (parseResult: ParseResult) => {
    try {
      setState('building');
      setError(null);

      // Build normalized import plan
      const importPlan = buildImportPlan(
        parseResult.persons,
        parseResult.resolvedFamilies,
        parseResult.validation
      );

      setPlan(importPlan);
      setState('ready');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to build import plan';
      setError(errorMessage);
      setState('error');
      console.error('[GEDCOM Import Plan] Build failed:', err);
    }
  };

  /**
   * Reset hook state
   */
  const reset = () => {
    setState('idle');
    setPlan(null);
    setError(null);
  };

  return {
    state,
    plan,
    buildPlan,
    reset,
    error,
  };
};
